import { memo, useState, useEffect } from 'react';
import { token } from '@atlaskit/tokens';
import { Icon, IconType } from '@components/shared/Icon';
import { css } from '@emotion/react';

import { CardStatus } from '@components/learning/LearningSidebar/constants';

import { Appearance, TextTooltip } from './TextTooltip';
import { LessonSections } from './LessonSections';
import { LessonItem, ProgressForLessons } from '../types';
import { isEntitySelected, mapLearningProgressToCardStatus } from '../utils/utils';
import { formatDurationFromMinutes } from '../utils/duration';
import { useDeviceSize } from '@hooks/useDeviceSize';

export const SIDEBAR_MOBILE_SCREEN_WIDTH = 480;

export interface LessonCardProps {
  lesson: LessonItem;
  progressForLesson?: ProgressForLessons['lessons'][0];
  expandedLessonIds?: string[];
  isLoading: boolean;
  courseSlug?: string;
  selectedSectionId?: string;
  currentLessonSlug?: string;
  onSelect: (courseSlug: string, lessonSlug?: string, sectionSlug?: string) => void;
  isCourse?: boolean;
}

export const dotStateMap: Record<string, IconType> = {
  [CardStatus.DEFAULT]: 'dot-default',
  [CardStatus.COMPLETED]: 'dot-completed',
};

export const LessonCard: React.FC<LessonCardProps> = memo((props) => {
  const {
    lesson: { sys, slug, title, duration },
    courseSlug,
    isLoading,
    progressForLesson,
    onSelect,
    expandedLessonIds,
    selectedSectionId,
    currentLessonSlug,
    isCourse,
  } = props;

  const deviceSize = useDeviceSize();
  const isMobile = deviceSize <= SIDEBAR_MOBILE_SCREEN_WIDTH;

  const isSelected = isEntitySelected('', slug);

  const status = mapLearningProgressToCardStatus(progressForLesson?.status);

  const [isLessonCardExpanded, setIsLessonCardExpanded] = useState(false);

  useEffect(() => {
    const isExpanded = expandedLessonIds?.includes(sys.id);
    setIsLessonCardExpanded(isExpanded || false);
  }, [expandedLessonIds]);

  const handleExpandClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsLessonCardExpanded(!isLessonCardExpanded);
  };

  const handleCardClick = (): void => {
    setIsLessonCardExpanded(true);
    onSelect(courseSlug || '', slug);
  };

  const renderStatusIndicator = (): JSX.Element => {
    if (status === CardStatus.COMPLETED) {
      return <Icon type="check-circle-outline" color={token('color.icon.success')} />;
    }

    return (
      <div css={[lessonTimeBadgeStyles, isSelected && lessonTimeBadgeSelectedStyles]}>
        {formatDurationFromMinutes(duration)}
      </div>
    );
  };

  return (
    <div data-testid="sidebar-lesson-card" css={[lessonCardStyles, isSelected && lessonCardSelectedStyles]}>
      <div
        css={[
          lessonCardHeaderStyles,
          isMobile && lessonCardHeaderMobileStyles,
          isCourse && isMobile && lessonCardHeaderCourseStyles,
        ]}
        onClick={handleCardClick}
      >
        {!isMobile && (
          <div css={lessonExpanderStyles} onClick={handleExpandClick}>
            <Icon type={isLessonCardExpanded ? 'chevron-down' : 'chevron-right'} color={token('color.icon')} />
          </div>
        )}
        <TextTooltip text={title} isSelected={isSelected} appearance={Appearance.LESSON} />
        <div css={lessonTimeBadgeContainerStyles}>{renderStatusIndicator()}</div>
      </div>
      <LessonSections
        lesson={props.lesson}
        progressForLesson={progressForLesson}
        isLoading={isLoading}
        courseSlug={courseSlug}
        selectedSectionId={selectedSectionId}
        currentLessonSlug={currentLessonSlug}
        onSelect={onSelect}
        isLessonCardExpanded={isLessonCardExpanded}
        isMobile={isMobile}
      />
    </div>
  );
});

const lessonCardStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',

  paddingTop: token('space.100'),
  paddingBottom: token('space.100'),
  paddingLeft: token('space.250'),
  paddingRight: token('space.100'),

  '&:active': {
    background: '#F1F2F4 !important',
  },

  '&:hover': {
    background: '#F7F8F9',
  },
});

const lessonCardSelectedStyles = css({
  background: token('color.background.input.hovered'),

  '&:active': {
    background: `${token('color.background.input.hovered')} !important`,
  },
});

const lessonCardHeaderStyles = css({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  cursor: 'pointer',
});

const lessonCardHeaderMobileStyles = css({
  marginLeft: `calc(${token('space.300')} + ${token('space.025')})`,
});

const lessonCardHeaderCourseStyles = css({
  marginLeft: 0,
});

const lessonExpanderStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '4px',
  cursor: 'pointer',
  marginRight: token('space.025'),

  '&:hover': {
    background: token('elevation.surface.pressed'),
  },
});

const lessonTimeBadgeStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  lineHeight: '18px',
  fontWeight: 400,
  color: token('color.text'),
  padding: `${token('space.025')} ${token('space.075')}`,
  borderRadius: '8px',
  background: token('color.background.neutral'),
  marginLeft: 'auto',
});

const lessonTimeBadgeSelectedStyles = css({
  color: token('color.text.brand'),
  background: token('color.background.brand.subtlest'),
});

const lessonTimeBadgeContainerStyles = css({
  marginLeft: 'auto',
});
